import moment from 'moment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Alert, LinearProgress, Stack, TextField, Typography } from '../../ui/core';
import api from '../../lib/api';
import useSdpFetch from './useSdpFetch';
import useQueryState, { isoDate, oneOf, useSetQueryParams } from './useQueryState';
import { EnumPill, GuardDescription, JsonBlock, Muted, SectionTitle } from './parts';
import { DateField, FilterBar, knownCity } from './StateTab';
import { RESPONSE_PALETTES, formatNum, formatTs } from './sdpFormatters';

const SCOPE_ORDER = { global: 0, city: 1, lane: 2 };

const sortInputs = (inputs) =>
  [...inputs].sort(
    (a, b) =>
      (SCOPE_ORDER[a.scope] ?? 9) - (SCOPE_ORDER[b.scope] ?? 9) ||
      String(a.scopeName || '').localeCompare(String(b.scopeName || '')) ||
      String(a.guard).localeCompare(String(b.guard)),
  );

const windowText = (start, end) => (start || end ? `${formatTs(start)} → ${formatTs(end)}` : '—');

const InputsTable = ({ inputs, computedAt }) => {
  if (!computedAt) return <Muted>No GUARDRAIL_INPUT synced yet: guards have nothing to evaluate.</Muted>;
  return (
    <>
      <Muted>{`Computed ${formatTs(computedAt)}`}</Muted>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scope</TableCell>
              <TableCell>Guard</TableCell>
              <TableCell>Metric</TableCell>
              <TableCell>Value vs threshold</TableCell>
              <TableCell>Distance to breach</TableCell>
              <TableCell>n</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>Response</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortInputs(inputs).map((input) => {
              const name = input.scopeName || input.scopeId || input.scope;
              const breaching = input.evaluable && typeof input.distanceToBreach === 'number' && input.distanceToBreach <= 0;
              return (
                <TableRow key={`${input.scope}-${input.scopeId}-${input.guard}`} data-testid={`input-row-${name}-${input.guard}`}>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px' }}>{name}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                      {input.scope}
                    </Typography>
                  </TableCell>
                  <TableCell>{input.guard}</TableCell>
                  <TableCell>{input.metric}</TableCell>
                  <TableCell>
                    {input.evaluable ? (
                      `${formatNum(input.value)} vs ${formatNum(input.threshold)}`
                    ) : (
                      <Typography component="span" color="text.secondary" sx={{ fontSize: '13px', fontStyle: 'italic' }}>
                        insufficient data
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    data-breaching={breaching}
                    sx={breaching ? { color: 'error.main', fontWeight: 600 } : undefined}
                  >
                    {input.evaluable ? formatNum(input.distanceToBreach) : '—'}
                  </TableCell>
                  <TableCell>{input.n ?? '—'}</TableCell>
                  <TableCell>{windowText(input.windowStart, input.windowEnd)}</TableCell>
                  <TableCell>
                    <EnumPill value={input.response} palettes={RESPONSE_PALETTES} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const EventsTable = ({ events }) => {
  if (events.length === 0) return <Muted>No guard events in this range.</Muted>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Opened</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>What happened</TableCell>
            <TableCell>Departure day</TableCell>
            <TableCell>Guard</TableCell>
            <TableCell>Value / threshold</TableCell>
            <TableCell>Response</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Evidence</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
              <TableCell>{formatTs(e.ts)}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '13px' }}>{e.scopeName || e.scopeId || e.scope}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                  {e.scope}
                </Typography>
              </TableCell>
              <TableCell>{e.description ? <GuardDescription event={e} /> : '—'}</TableCell>
              <TableCell>{e.departureDate || '—'}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '13px' }}>{`${e.guard} (tier ${e.tier ?? '—'})${e.escalated ? ' escalated' : ''}`}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                  {e.metric}
                </Typography>
              </TableCell>
              <TableCell>{`${formatNum(e.value)} / ${formatNum(e.threshold)}`}</TableCell>
              <TableCell>
                <EnumPill value={e.response} palettes={RESPONSE_PALETTES} />
              </TableCell>
              <TableCell>
                {e.clearedAt ? (
                  <>
                    <Typography sx={{ fontSize: '13px' }}>{`cleared by ${e.clearedBy || '—'}`}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                      {formatTs(e.clearedAt)}
                    </Typography>
                    {e.clearedText && (
                      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                        {e.clearedText}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}>open</Typography>
                )}
              </TableCell>
              <TableCell>
                <JsonBlock value={e.evidence} label="evidence" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Only set keys reach the API: scope/scopeId filter both the inputs and the
// events; from/to bound the events' ts (API default: last 28 days).
const buildQuery = ({ scope, scopeId, from, to }) => {
  const query = {};
  if (scope) query.scope = scope;
  if (scope === 'city' && scopeId) query.scopeId = scopeId;
  if (from) query.from = moment(from).startOf('day').toISOString();
  if (to) query.to = moment(to).endOf('day').toISOString();
  return query;
};

const GuardsTab = ({ cities = [] }) => {
  const [scope] = useQueryState('scope', { parse: oneOf(['city', 'lane', 'global']) });
  // A city only means something under the city scope.
  const [cityParam, setScopeId] = useQueryState('scopeId', { parse: knownCity(cities) });
  const scopeId = scope === 'city' ? cityParam : '';
  const [from, setFrom] = useQueryState('from', { parse: isoDate });
  const [to, setTo] = useQueryState('to', { parse: isoDate });
  const setParams = useSetQueryParams();

  const query = buildQuery({ scope, scopeId, from, to });
  const { data, loading, error } = useSdpFetch(() => api.supplyDemandPace.getGuards(query), JSON.stringify(query));

  const onScopeChange = (value) => setParams({ scope: value, scopeId: null });

  return (
    <Stack spacing="8px">
      <FilterBar>
        <TextField
          select
          name="scope"
          label="Scope"
          size="small"
          value={scope}
          onChange={(e) => onScopeChange(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140 }}
        >
          <option value="">all</option>
          <option value="city">city</option>
          <option value="lane">lane</option>
          <option value="global">global</option>
        </TextField>
        {scope === 'city' && (
          <TextField
            select
            name="scopeId"
            label="City"
            size="small"
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c.cityId} value={c.cityId}>
                {c.cityName || c.cityId}
              </option>
            ))}
          </TextField>
        )}
        <DateField name="from" label="Events from" value={from} onChange={setFrom} />
        <DateField name="to" label="Events to" value={to} onChange={setTo} />
      </FilterBar>
      <Muted>
        {`Scope filters apply to the inputs and the event history; the date range applies to events${
          !from && !to ? ' (default: last 28 days)' : ''
        }.`}
      </Muted>
      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <SectionTitle>Latest guard inputs</SectionTitle>
      {data && <InputsTable inputs={data.inputs || []} computedAt={data.inputsComputedAt} />}

      <SectionTitle>Guard event history</SectionTitle>
      {data && <EventsTable events={data.events || []} />}
    </Stack>
  );
};

export default GuardsTab;
